// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {BannerEl} from 'common/components/banner';
import {BannerType} from 'common/components/banner/types';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {InputEl} from 'common/components/input';
import {ModalEl} from 'common/components/modal';
import {SelectEl} from 'common/components/select';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerColor} from 'common/components/spinner/types';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {DisplayType, Placement} from 'common/components/tooltip/types';
import {ToggleEl} from 'common/components/toggle';
import {ToggleTheme} from 'common/components/toggle/types';
import {FileUploaderEl} from 'common/components/file-uploader';
import {
  ExpiresModeOptions,
  ExpiresModeOptionsI18nKey,
  ExpiresUnit,
  ExpiresUnitI18nKey,
} from 'common/types/reports/service-accounts';
import {AutocompleteType} from 'common/components/input/types';
import {UserRow} from 'common/widgets/invite-users-modal/types';
import {CsvFormatInfoEl} from 'common/widgets/invite-users-modal/csv-format-info';
import {UpgradePlanEl} from 'common/widgets/invite-users-modal/upgrade-plan';
import {UserRoleI18nKey} from 'common/report/util/saved-entities/constants';

import {CopyToClipboardEl} from '../copy-to-clipboard';
import type {InviteUsersModal} from './index';

export function template(this: InviteUsersModal) {
  const {currentTab, error, isLoading, orgs, selectedOrg, serviceAccount, userFile, userRows} = this.state;
  const isServiceAccount = this.helpers.isServiceAccount();
  const isSingleProject = !!this.getProjectIdIfSingleProject();
  const modalHeader = isSingleProject
    ? this.t(`settings.org-settings.invite-users.invite`)
    : this.t(`settings.org-settings.invite-users.invite-new-users`);

  const InviteUsersRow = (props: {row: UserRow; idx: number}) => (
    <>
      <div sel='.invite-users-row'>
        <>
          <div sel='.invite-users-column'>
            {isServiceAccount ? (
              <>
                <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.name`)}</div>
                <div sel='.invite-users-input-container'>
                  <InputEl
                    sel='.invite-users-service-account-name'
                    attrs={{
                      'error-message': props.row.nameErrorMsg,
                      'validation-status': this.helpers.serviceAccountNameValidationStatus(props.row),
                    }}
                    props={{
                      value: props.row.name,
                    }}
                    on={{
                      input: (ev) => this.helpers.updateServiceAccountName(ev, props.row),
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                {!props.idx && (
                  <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.email-address`)}</div>
                )}
                <div sel='.invite-users-input-container'>
                  <InputEl
                    sel='.invite-users-input.invite-users-email-input'
                    attrs={{
                      type: `text`,
                      /* autocomplete=off does not work as of Chrome 34 */
                      autocomplete: AutocompleteType.InviteUsersAutocompleteOff,
                    }}
                    props={{
                      value: props.row.email,
                    }}
                    on={{
                      input: (ev) => this.helpers.updateUserEmail(ev, props.row),
                      focus: this.helpers.clearErrors,
                      blur: (ev) => this.helpers.updateEmailError(ev, props.row),
                    }}
                  />
                  {(props.row.emailError || props.row.emailInfo) &&
                    props.row === userRows.find((userRow) => userRow.email === props.row.email) && (
                      <TooltipEl
                        attrs={{
                          'always-show': true,
                          type: props.row.emailInfo ? DisplayType.Default : DisplayType.Error,
                          placement: Placement.Left,
                        }}
                      >
                        {(() => {
                          if (props.row.emailErrorMsg) {
                            return props.row.emailErrorMsg;
                          }
                          return props.row.email && props.row.email.length
                            ? this.t(`settings.org-settings.invite-users.enter-valid-email`)
                            : this.t(`settings.org-settings.invite-users.enter-email`);
                        })()}
                      </TooltipEl>
                    )}
                </div>
              </>
            )}
          </div>
          <div sel='.invite-users-column'>
            {!props.idx && <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.org-role`)}</div>}
            <div sel='.invite-users-input-container'>
              <SelectEl
                attrs={{
                  'selected-option': props.row.orgRole
                    ? this.t(UserRoleI18nKey[props.row.orgRole])
                    : this.t(`settings.org-settings.invite-users.select-role`),
                  'menu-height': 350 /* menu with 4 roles is just over 200px, looks weird */,
                  size: ButtonSize.Large,
                }}
                props={{
                  sections: this.helpers.getOrgRoleSections(props.row),
                }}
                on={{
                  change: (ev) => this.helpers.selectOrgRole(ev, props.row),
                  focus: this.helpers.clearErrors,
                }}
              />
            </div>
            {props.row.orgRoleError && props.row === userRows.find((row) => row.orgRoleError) && (
              <TooltipEl
                attrs={{
                  'always-show': true,
                  type: DisplayType.Error,
                  placement: Placement.Top,
                }}
              >
                {this.t(`settings.org-settings.invite-users.please-select-role`)}
              </TooltipEl>
            )}
          </div>
          {!this.attr(`exclude-project-selections`) && currentTab === this.helpers.TABS.PROJECT && (
            <>
              {!isSingleProject && (
                <div sel='.invite-users-column' style={{width: `250px`}}>
                  {(() => {
                    const numProjects = props.row.projectIds.length;

                    return (
                      <>
                        {!props.idx && (
                          <div sel='.invite-users-title'>{this.t(`mixology.project`, {context: `plural`})}</div>
                        )}
                        <div sel='.invite-users-input-container'>
                          <SelectEl
                            style={{width: `250px`}}
                            attrs={{
                              'selected-option': numProjects
                                ? this.t(`mixology.project-count`, {count: numProjects})
                                : this.t(`settings.org-settings.invite-users.select-projects`),
                              multiple: true,
                              'show-select-all': true,
                              size: ButtonSize.Large,
                              'menu-width': 250,
                            }}
                            props={{
                              sections: this.helpers.getProjectSections(props.row),
                            }}
                            on={{
                              change: (ev) => this.helpers.toggleProjectSelected(ev, props.row),
                              selectAll: (ev) => this.helpers.toggleAllProjectsSelected(ev, props.row),
                              focus: this.helpers.clearErrors,
                            }}
                          />
                        </div>
                        {props.row.projectError && props.row === userRows.find((row) => row.projectError) && (
                          <TooltipEl
                            attrs={{
                              'always-show': true,
                              type: DisplayType.Error,
                              placement: Placement.Top,
                            }}
                          >
                            {this.t(`settings.org-settings.invite-users.please-select-project`)}
                          </TooltipEl>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              <div sel='.invite-users-column'>
                {!props.idx && (
                  <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.project-role`)}</div>
                )}
                <div sel='.invite-users-input-container'>
                  <SelectEl
                    attrs={{
                      'selected-option': props.row.projectRole
                        ? this.t(UserRoleI18nKey[props.row.projectRole])
                        : this.t(`settings.org-settings.invite-users.select-role`),
                      'menu-height': 300 /* menu with 4 roles is just over 200px, looks weird */,
                      size: ButtonSize.Large,
                    }}
                    props={{
                      sections: this.helpers.getProjectRoleSections(),
                    }}
                    on={{
                      change: (ev) => this.helpers.selectProjectRole(ev, props.row),
                      focus: this.helpers.clearErrors,
                    }}
                  />
                  {props.row.projectRoleError && props.row === userRows.find((row) => row.projectRoleError) && (
                    <TooltipEl
                      attrs={{
                        'always-show': true,
                        type: DisplayType.Error,
                        placement: Placement.Right,
                      }}
                    >
                      {this.t(`settings.org-settings.invite-users.please-select-role`)}
                    </TooltipEl>
                  )}
                </div>
              </div>
              {isServiceAccount && (
                <div sel='.invite-users-column.invite-users-expires-column'>
                  <div sel='.invite-users-expires-container'>
                    <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.expires`)}</div>
                    {props.row?.expires ? (
                      <div sel='.expires-form'>
                        <InputEl
                          sel='.invite-users-expires-interval-input'
                          attrs={{type: `number`}}
                          props={{value: String(props.row.expires.interval)}}
                          on={{input: (ev) => this.helpers.updateExpiresInterval(ev, props.row)}}
                        />
                        <SelectEl
                          sel='.invite-users-expires-unit-selector'
                          attrs={{
                            'menu-width': 100,
                          }}
                          props={{
                            sections: [
                              {
                                items: Object.values(ExpiresUnit).map((unit) => {
                                  return {
                                    label: this.t(ExpiresUnitI18nKey[unit]),
                                    value: unit,
                                    isSelected: unit === props.row.expires.unit,
                                  };
                                }),
                              },
                            ],
                          }}
                          on={{change: (ev) => this.helpers.selectExpiresUnit(ev, props.row)}}
                        />
                      </div>
                    ) : (
                      <SelectEl
                        sel='.invite-users-expires-mode-selector'
                        attrs={{
                          'menu-width': 100,
                        }}
                        props={{
                          sections: [
                            {
                              items: Object.values(ExpiresModeOptions).map((mode) => {
                                return {
                                  label: this.t(ExpiresModeOptionsI18nKey[mode]),
                                  value: mode,
                                  isSelected: mode === ExpiresModeOptions.Never,
                                };
                              }),
                            },
                          ],
                        }}
                        on={{change: (ev) => this.helpers.selectExpirationMode(ev, props.row)}}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {currentTab === this.helpers.TABS.TEAM && (
            <div sel='.invite-users-column'>
              {(() => {
                const numTeams = props.row.teamNames.length;

                return (
                  <>
                    {!props.idx && (
                      <div sel='.invite-users-title'>{this.t(`settings.org-settings.invite-users.teams`)}</div>
                    )}
                    <SelectEl
                      attrs={{
                        'selected-option': numTeams
                          ? this.t(`settings.org-settings.invite-users.team-count`, {count: numTeams})
                          : this.t(`settings.org-settings.invite-users.select-teams`),
                        multiple: true,
                        'show-select-all': true,
                        size: ButtonSize.Large,
                      }}
                      props={{
                        sections: this.helpers.getTeamSections(props.row),
                      }}
                      on={{
                        change: (ev) => this.helpers.toggleTeamSelected(ev, props.row),
                        selectAll: (ev) => this.helpers.toggleAllTeamsSelected(ev, props.row),
                      }}
                    />
                  </>
                );
              })()}
            </div>
          )}
          {userRows.length > 1 && (
            <div sel='.invite-users-remove-button' on={{click: () => this.helpers.clickedDeleteButton(props.row)}}>
              <SvgIconEl attrs={{icon: `x`}} />
            </div>
          )}
        </>
      </div>
      {props.row.projectIdsToDowngrade?.length > 0 &&
        (() => {
          const downgradedProjectsString = this.helpers.getDowngradedProjectsString(props.row);
          const allDowngradedProjectsString = this.helpers.getAllDowngradedProjectsString(props.row);

          return (
            <div sel='.invite-users-row-error'>
              <div sel='.invite-users-error-title'>
                {this.t(`settings.org-settings.invite-users.cant-assign-owner-role`, {
                  numProjects: props.row.projectIdsToDowngrade.length,
                })}
              </div>
              {props.row.seeAll ? (
                <div sel='.projects-list'>{allDowngradedProjectsString}</div>
              ) : (
                <>
                  <div sel='.project-list'>{downgradedProjectsString}</div>
                  {downgradedProjectsString.length < allDowngradedProjectsString.length && (
                    <div
                      sel='.invite-users-see-all'
                      on={{click: () => this.helpers.seeAllDowngradedProjects(props.idx)}}
                    >
                      {this.t(`settings.org-settings.invite-users.see-all`)}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
    </>
  );

  return (
    <div sel='.settings-screen-modal'>
      <ModalEl
        sel='.invite-users-modal'
        attrs={{open: this.attr(`open`)}}
        on={{change: this.helpers.modalVisibilityChanged}}
      >
        {isServiceAccount ? (
          <>
            <div sel='.mp-modal-header'>
              {isLoading ? (
                <SpinnerEl attrs={{color: SpinnerColor.White}} />
              ) : (
                this.t(`settings.org-settings.invite-users.create-service-account`)
              )}
            </div>
            {error ? (
              <BannerEl
                sel='.invite-users-error-banner'
                attrs={{
                  theme: `warn`,
                  type: BannerType.Embedded,
                  'header-text': this.t(`settings.org-settings.invite-users.create-service-account-error`),
                  'body-text': error,
                }}
              />
            ) : null}
            {!serviceAccount ? (
              <>
                <div sel='.invite-users-add-users'>
                  {userRows.map((row, idx) => (
                    <InviteUsersRow row={row} idx={idx} />
                  ))}
                </div>
                <div sel='.mp-modal-bottom-buttons'>
                  <ButtonEl
                    sel='.mp-modal-button'
                    attrs={{
                      theme: ButtonTheme.Primary,
                      disabled: isLoading || !this.attr(`open`) || !selectedOrg || this.helpers.hasInvalidName(),
                    }}
                    on={{click: this.helpers.clickedSave}}
                  >
                    {this.t(`common.create`)}
                  </ButtonEl>
                  <ButtonEl sel='.mp-modal-button' on={{click: this.helpers.clickedCancel}}>
                    {this.t(`common.cancel`)}
                  </ButtonEl>
                </div>
              </>
            ) : (
              <>
                <div sel='.copy-container'>
                  <div sel='.copy-token-warning.mp-font-weight-bold'>
                    {this.t(`settings.org-settings.invite-users.save-secret-info`)}
                  </div>
                  <div sel='.modal-content-container'>
                    <div sel='.modal-content-row'>
                      <CopyToClipboardEl
                        attrs={{
                          label: this.t(`settings.org-settings.invite-users.username`),
                          value: serviceAccount.username,
                        }}
                      />
                    </div>
                    <div sel='.modal-content-row'>
                      <CopyToClipboardEl
                        attrs={{
                          label: this.t(`settings.org-settings.invite-users.secret`),
                          value: serviceAccount.secret,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div sel='.mp-modal-bottom-buttons'>
                  <ButtonEl sel='.mp-modal-button' on={{click: this.helpers.handleClickedDone}}>
                    {this.t(`common.done`)}
                  </ButtonEl>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div sel='.mp-modal-header'>
              {isLoading ? <SpinnerEl attrs={{color: SpinnerColor.White}} /> : modalHeader}
            </div>
            {(!this.attr(`exclude-teams`) || !this.attr(`exclude-bulk-invite`)) && (
              <div sel='.mp-modal-tab-section'>
                <ToggleEl
                  attrs={{
                    selected: currentTab,
                    theme: ToggleTheme.TabBar,
                  }}
                  on={{
                    select: this.helpers.selectTab,
                  }}
                >
                  {/* eslint-disable local-rules/jsx-usage */}
                  <mp-toggle-option attrs={{value: this.helpers.TABS.PROJECT}}>
                    {isSingleProject
                      ? this.t(`settings.org-settings.invite-users.invite`)
                      : this.t(`settings.org-settings.invite-users.invite-to-projects`)}
                  </mp-toggle-option>
                  {!this.attr(`exclude-teams`) && (
                    <mp-toggle-option attrs={{value: this.helpers.TABS.TEAM}}>
                      {this.t(`settings.org-settings.invite-users.invite-to-teams`)}
                    </mp-toggle-option>
                  )}
                  {!this.attr(`exclude-bulk-invite`) && (
                    <mp-toggle-option attrs={{value: this.helpers.TABS.BULK}}>
                      {this.t(`settings.org-settings.invite-users.bulk-invite`)}
                    </mp-toggle-option>
                  )}
                  {/* eslint-enable local-rules/jsx-usage */}
                </ToggleEl>
              </div>
            )}
            {orgs.length > 1 ? (
              <div sel='.invite-users-select-org'>
                <div sel='.invite-users-title'>{this.t(`mixology.organization`).toLowerCase()}</div>
                <SelectEl
                  attrs={{
                    'selected-option': selectedOrg
                      ? selectedOrg.name
                      : this.t(`settings.org-settings.invite-users.select-org`),
                    size: ButtonSize.Large,
                  }}
                  props={{
                    sections: this.helpers.getOrgSections(),
                  }}
                  on={{
                    change: this.helpers.selectOrg,
                  }}
                />
              </div>
            ) : null}
            <div sel='.invite-users-add-users' class={{'invite-users-hidden': !selectedOrg}}>
              {selectedOrg && this.helpers.isOverSeatCount() ? (
                <div sel='.invite-users-warning-text-container'>
                  <SvgIconEl attrs={{icon: `warning`}} />
                  <div sel='.invite-users-warning-text'>
                    {this.t(`settings.org-settings.invite-users.out-of-seats-upsell`)}
                    {` `}
                    {this.helpers.hasBillingPerms() ? (
                      <UpgradePlanEl onClickUpsell={this.helpers.trackUpsellClick} />
                    ) : (
                      this.t(`settings.org-settings.invite-users.contact-admin-to-upgrade`)
                    )}
                  </div>
                </div>
              ) : null}
              {currentTab === this.helpers.TABS.PROJECT || currentTab === this.helpers.TABS.TEAM ? (
                <>
                  {selectedOrg ? userRows.map((row, idx) => <InviteUsersRow row={row} idx={idx} />) : null}
                  <div sel='.invite-users-add-another-user'>
                    <div sel='.invite-users-add-another-user-button' on={{click: this.helpers.clickedAddMoreUser}}>
                      <SvgIconEl attrs={{icon: `plus`}} />
                      {this.t(`settings.org-settings.invite-users.add-another-user`)}
                    </div>
                    <div sel='.invite-users-add-another-user-alignment' />
                  </div>
                </>
              ) : null}
              {currentTab === this.helpers.TABS.BULK ? (
                <div sel='.invite-users-bulk-add-users'>
                  <div sel='.invite-users-bulk-add-users-title'>
                    {this.t(`settings.org-settings.invite-users.file-name`)}
                  </div>
                  <div sel='.invite-users-bulk-add-file-uploader'>
                    <FileUploaderEl
                      attrs={{
                        accept: `text/csv`,
                        label: this.t(`settings.org-settings.invite-users.select-csv`),
                        'completed-filename': userFile && userFile.name ? userFile.name : ``,
                        'enable-delete': true,
                        'max-filename-chars': 120,
                      }}
                      on={{change: this.helpers.fileStatusChanged, delete: this.helpers.removeSelectedFile}}
                    />
                    {userFile && userFile.error ? (
                      <TooltipEl
                        attrs={{
                          'always-show': true,
                          type: DisplayType.Error,
                          placement: Placement.Right,
                        }}
                      >
                        {userFile.errorMsg || this.t(`settings.org-settings.invite-users.please-select-csv`)}
                      </TooltipEl>
                    ) : null}
                  </div>
                  <div sel='.invite-users-bulk-add-users-subtitle'>
                    <CsvFormatInfoEl />
                  </div>
                </div>
              ) : null}
            </div>
            <div sel='.mp-modal-bottom-buttons'>
              <ButtonEl
                sel='.mp-modal-button'
                attrs={{
                  theme: ButtonTheme.Primary,
                  disabled: isLoading || !this.attr(`open`) || this.helpers.isOverSeatCount() || !selectedOrg,
                }}
                on={{click: this.helpers.clickedSave}}
              >
                {currentTab === this.helpers.TABS.BULK
                  ? this.t(`settings.org-settings.invite-users.upload`)
                  : this.t(`settings.org-settings.invite-users.invite`)}
              </ButtonEl>
              <ButtonEl sel='.mp-modal-button' on={{click: this.helpers.clickedCancel}}>
                {this.t(`common.cancel`)}
              </ButtonEl>
            </div>
          </>
        )}
      </ModalEl>
    </div>
  );
}
